import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";
import { functor, isDefined, head, hexToRGBA } from "../utils";
import { plotDataLengthBarWidth } from "../utils/barWidth";

class VolumeSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  drawOnCanvas(ctx, moreProps) {
    const bars = this.getBars(moreProps);
    this.drawBarsOnCanvas(ctx, bars, moreProps);
  }

  drawBarsOnCanvas(ctx, bars, moreProps) {
    const hovering = moreProps.hovering;
    bars.forEach((bar) => {
      const fill =
        hovering && bar.isCursorInside ? hexToRGBA(bar.fill, 0.85) : bar.fill;
      ctx.fillStyle = fill;
      ctx.fillRect(bar.x, bar.y, bar.width, bar.height);
      if (hovering && bar.isCursorInside) {
        ctx.lineWidth = 1;
        ctx.strokeStyle = hexToRGBA(bar.fill, 1);
        ctx.strokeRect(bar.x, bar.y, bar.width, bar.height);
      }
    });
  }

  renderSVG(moreProps) {
    const { className } = this.props;
    const bars = this.getBars(moreProps);

    return (
      <g className={className}>
        {bars.map((bar, idx) => (
          <rect
            key={idx}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            fill={bar.fill}
            stroke={bar.isCursorInside ? bar.fill : "none"}
            strokeWidth={bar.isCursorInside ? 1 : 0}
            fillOpacity={bar.isCursorInside ? 0.85 : 1}
          />
        ))}
      </g>
    );
  }

  getBars(moreProps) {
    const { yAccessor, fill, baseAt } = this.props;
    const {
      xScale,
      xAccessor,
      plotData,
      chartConfig: { yScale, origin },
      mouseXY,
    } = moreProps;

    const getFill = functor(fill);
    const getBase = functor(baseAt);

    const barWidth = Math.max(1, plotDataLengthBarWidth(this.props, moreProps));
    const offset = Math.floor(barWidth / 2);

    const bars = plotData
      .filter((d) => isDefined(yAccessor(d)))
      .map((d) => {
        const volumeData = yAccessor(d);
        if (!volumeData || !isDefined(volumeData.volume)) return null;

        const volume = volumeData.volume;
        const direction = volumeData.direction || "up";

        const x = Math.round(xScale(xAccessor(d))) - offset;
        const baseY = getBase(xScale, yScale, d);
        const topY = yScale(volume);
        const y = Math.min(baseY, topY);
        const height = Math.abs(baseY - topY);

        const screenX = x + origin[0];
        const screenY = y + origin[1];

        const pointer = Array.isArray(mouseXY)
          ? { x: mouseXY[0] + origin[0], y: mouseXY[1] + origin[1] }
          : null;

        const fillColor = getFill({ direction });

        const isCursorInside = pointer
          ? pointer.x >= screenX &&
            pointer.x <= screenX + barWidth &&
            pointer.y >= screenY &&
            pointer.y <= screenY + height
          : false;

        return {
          x,
          y,
          width: barWidth,
          height,
          fill: fillColor,
          direction,
          isCursorInside,
          screenX,
          screenY,
        };
      })
      .filter((bar) => bar !== null);

    return bars;
  }

  render() {
    const {
      clip,
      onContextMenu,
      onDoubleClick,
      onHover,
      onUnHover,
      onClick,
      highlightOnHover,
      hoverTolerance,
    } = this.props;
    const needsHover =
      highlightOnHover ||
      onHover ||
      onUnHover ||
      onContextMenu ||
      onDoubleClick ||
      onClick;
    const hoverProps = needsHover
      ? {
          isHover: this.isHover,
          drawOn: ["mousemove", "pan", "drag"],
          canvasToDraw: getMouseCanvas,
        }
      : {
          drawOn: ["pan", "drag"],
          canvasToDraw: getAxisCanvas,
        };

    return (
      <GenericChartComponent
        clip={clip}
        svgDraw={this.renderSVG}
        canvasDraw={this.drawOnCanvas}
        onHover={onHover}
        onUnHover={onUnHover}
        onClickWhenHover={onClick}
        onDoubleClickWhenHover={onDoubleClick}
        onContextMenuWhenHover={onContextMenu}
        {...hoverProps}
      />
    );
  }
}

VolumeSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  fill: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
  widthRatio: PropTypes.number,
  baseAt: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  clip: PropTypes.bool,
  onContextMenu: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
  onClick: PropTypes.func,
  highlightOnHover: PropTypes.bool,
  hoverTolerance: PropTypes.number,
};

VolumeSeries.defaultProps = {
  className: "react-stockcharts-volume-series",
  fill: ({ direction }) => (direction === "up" ? "#6BA583" : "#FF0000"),
  widthRatio: 0.8,
  baseAt: (xScale, yScale /* , d */) => head(yScale.range()),
  clip: true,
  highlightOnHover: true,
  hoverTolerance: 4,
};

VolumeSeries.prototype.isHover = function (moreProps) {
  const {
    highlightOnHover,
    onHover,
    onUnHover,
    onContextMenu,
    onDoubleClick,
    onClick,
    hoverTolerance,
  } = this.props;

  const needsHover =
    highlightOnHover ||
    onHover ||
    onUnHover ||
    onContextMenu ||
    onDoubleClick ||
    onClick;
  if (!needsHover) return false;

  const {
    mouseXY,
    chartConfig: { origin },
  } = moreProps;
  if (!Array.isArray(mouseXY)) return false;

  const pointer = {
    x: mouseXY[0] + origin[0],
    y: mouseXY[1] + origin[1],
  };

  const bars = this.getBars(moreProps);
  if (!bars.length || !pointer) return false;
  const tolerance = Number.isFinite(hoverTolerance) ? hoverTolerance : 4;

  for (let i = 0; i < bars.length; i += 1) {
    const bar = bars[i];
    if (!bar) continue;
    const left = (bar.screenX ?? bar.x) - tolerance;
    const right = (bar.screenX ?? bar.x) + bar.width + tolerance;
    const top = (bar.screenY ?? bar.y) - tolerance;
    const bottom = (bar.screenY ?? bar.y) + bar.height + tolerance;
    if (
      pointer.x >= left &&
      pointer.x <= right &&
      pointer.y >= top &&
      pointer.y <= bottom
    ) {
      return true;
    }
  }
  return false;
};

export default VolumeSeries;
